import React, { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useParams } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  InputAdornment,
  IconButton,
  Breadcrumbs,
  Link,
  Drawer,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Tooltip,
  Stack,
  Checkbox,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  LinearProgress,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SearchIcon from "@mui/icons-material/Search";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import FirstPageIcon from "@mui/icons-material/FirstPage";
import LastPageIcon from "@mui/icons-material/LastPage";
import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import {
  getControls,
  getChecks,
  clearSuppressionState,
  bulkUpsertSuppressions,
  clearBulkSuppressionMessages,
  getSchedules,
} from "../../../../../store/suppression/suppressionSlice";
import { formatScheduleName } from "../../utils/scheduleNameFormatter";
import store from "../../../../../store";

const Controls = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { scheduleId, frameworkId } = useParams();

  const { 
    controls, 
    isFetchingControls, 
    fetchControlsError, 
    checks, 
    isFetchingChecks, 
    fetchChecksError,
    isSavingSuppressions,
    saveSuppressionsError,
    saveSuppressionsSuccess,
    schedules,
    isFetchingSchedules: isFetchingSchedulesRedux,
    fetchSchedulesError,
  } = useSelector((state) => state.suppression);

  // Get selected framework and schedule from sessionStorage
  const [selectedFramework, setSelectedFramework] = useState(null);
  const [selectedSchedule, setSelectedSchedule] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  // Pagination state
  const [page, setPage] = useState(1);
  const API_PAGE_SIZE = 100; // API fetches 100 records at a time
  const DEFAULT_PAGE_SIZE = 25;
  
  // Get page size from localStorage or use default
  const getStoredPageSize = () => {
    const stored = localStorage.getItem("suppression_controls_page_size");
    return stored ? parseInt(stored, 10) : DEFAULT_PAGE_SIZE;
  };
  
  const [pageSize, setPageSize] = useState(getStoredPageSize());
  
  // Store all controls data and track fetched API pages
  const [allControls, setAllControls] = useState(null);
  const [fetchedApiPages, setFetchedApiPages] = useState(new Set([1]));

  // Selection state
  const [selectedControls, setSelectedControls] = useState(new Set()); // Set of control_ids
  const [selectedChecks, setSelectedChecks] = useState({}); // { control_id: Set of check_ids }
  
  // Track initial state to detect changes
  const [initialSelectedControls, setInitialSelectedControls] = useState(new Set());
  const [initialSelectedChecks, setInitialSelectedChecks] = useState({});
  
  // Track initial state for checks in the current drawer (per control)
  const [initialChecksForCurrentControl, setInitialChecksForCurrentControl] = useState(new Set());

  // Checks drawer state
  const [checksDrawerOpen, setChecksDrawerOpen] = useState(false);
  const [selectedControl, setSelectedControl] = useState(null);
  const [allChecks, setAllChecks] = useState(null); // Store all checks for client-side filtering
  
  // Checks filters
  const [checksSearchTerm, setChecksSearchTerm] = useState("");
  const [checksSeverity, setChecksSeverity] = useState("");
  const [checksService, setChecksService] = useState("");
  const [checksPage, setChecksPage] = useState(0);
  const checksPageSize = 50;

  // Snackbar state
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Save dialog state for controls
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [saveStatus, setSaveStatus] = useState({
    step: 0, // 0: not started, 1: deleting, 2: creating, 3: refreshing, 4: done
    deleteMessage: "",
    createMessage: "",
    error: null,
  });

  // Reset confirmation dialog state
  const [resetConfirmDialogOpen, setResetConfirmDialogOpen] = useState(false);

  // Save dialog state for checks
  const [saveChecksDialogOpen, setSaveChecksDialogOpen] = useState(false);
  const [saveChecksStatus, setSaveChecksStatus] = useState({
    step: 0,
    deleteMessage: "",
    createMessage: "",
    error: null,
  });

  // Schedule selection dialog state
  const [scheduleSelectionDialogOpen, setScheduleSelectionDialogOpen] = useState(false);
  const [availableSchedules, setAvailableSchedules] = useState([]);
  const [selectedSchedulesToApply, setSelectedSchedulesToApply] = useState(new Set());
  const [isFetchingSchedules, setIsFetchingSchedules] = useState(false);
  const [pendingSaveAction, setPendingSaveAction] = useState(null); // 'controls' or 'checks'

  useEffect(() => {
    // Retrieve framework and schedule data from sessionStorage
    const frameworkData = sessionStorage.getItem("selectedFramework");
    if (frameworkData) {
      try {
        const data = JSON.parse(frameworkData);
        setSelectedFramework(data.framework);
        setSelectedSchedule(data.schedule);
      } catch (error) {
        console.error("Error parsing selectedFramework:", error);
      }
    }
  }, []);

  // Fetch controls on mount
  useEffect(() => {
    if (frameworkId && scheduleId) {
      const params = {
        schedule_id: scheduleId,
        page: 1,
        page_size: API_PAGE_SIZE,
      };

      dispatch(getControls({ complianceaddon_id: frameworkId, params }));
    }
  }, [dispatch, frameworkId, scheduleId]);
  
  // Check if we need to fetch more data when page changes
  useEffect(() => {
    if (!allControls?.items || !frameworkId || !scheduleId) return;
    
    // Calculate which API page we need based on current UI page and page size
    const recordsNeeded = page * pageSize;
    const apiPageNeeded = Math.ceil(recordsNeeded / API_PAGE_SIZE);
    
    // If we haven't fetched this API page yet, fetch it
    if (apiPageNeeded > 0 && !fetchedApiPages.has(apiPageNeeded)) {
      const params = {
        schedule_id: scheduleId,
        page: apiPageNeeded,
        page_size: API_PAGE_SIZE,
      };

      if (searchTerm) {
        params.search = searchTerm;
      }

      dispatch(getControls({ complianceaddon_id: frameworkId, params }));
      setFetchedApiPages((prev) => new Set([...prev, apiPageNeeded]));
    }
  }, [page, pageSize, allControls, fetchedApiPages, dispatch, frameworkId, scheduleId, searchTerm]);
  
  // Track if we just saved (to reinitialize state after refresh)
  const [justSaved, setJustSaved] = useState(false);

  // Update allControls when controls data is received (merge with existing data)
  useEffect(() => {
    if (controls && !isFetchingControls && controls.items) {
      setAllControls((prev) => {
        let newData;
        
        if (!prev) {
          newData = controls;
        } else {
          // Merge: update existing items and add new ones
          const existingMap = new Map(prev.items.map((item) => [item.control_id, item]));
          
          // Update existing items with new data (important for is_suppressed changes)
          controls.items.forEach((newItem) => {
            existingMap.set(newItem.control_id, newItem);
          });
          
          newData = {
            ...controls,
            items: Array.from(existingMap.values()),
            total: Math.max(prev.total || 0, controls.total || 0),
          };
        }
        
        // Initialize or update state based on suppressed controls from fresh API data
        const suppressedControls = new Set();
        newData.items.forEach((control) => {
          if (control.is_suppressed && !control.check_id) {
            // Control-level suppression
            suppressedControls.add(control.control_id);
          }
        });
        
        // If we just saved, reinitialize both initial and current state from fresh API data
        if (justSaved) {
          console.log("Reinitializing state after save with suppressed controls:", Array.from(suppressedControls));
          setInitialSelectedControls(new Set(suppressedControls));
          setSelectedControls(new Set(suppressedControls));
          setInitialSelectedChecks({});
          setJustSaved(false);
        } else if (!prev || initialSelectedControls.size === 0) {
          // First load - initialize state
          setInitialSelectedControls(new Set(suppressedControls));
          setSelectedControls(new Set(suppressedControls));
          setInitialSelectedChecks({});
        } else {
          // Update selected controls based on current data from API
          setSelectedControls((prevSelected) => {
            const newSelected = new Set();
            newData.items.forEach((control) => {
              if (control.is_suppressed && !control.check_id) {
                newSelected.add(control.control_id);
              }
            });
            return newSelected;
          });
        }
        
        return newData;
      });
    }
  }, [controls, isFetchingControls, initialSelectedControls.size, justSaved]);

  // Clear state on unmount
  useEffect(() => {
    return () => {
      dispatch(clearSuppressionState());
    };
  }, [dispatch]);

  // Handle back navigation
  const handleBack = () => {
    history.push(`/configuration/suppression/frameworks/${scheduleId}`);
  };

  // Handle breadcrumb navigation
  const handleBreadcrumbClick = (path) => {
    history.push(path);
  };

  // Filter and paginate controls client-side
  const filteredControls = useMemo(() => {
    if (!allControls?.items) return { items: [], total: 0, page: 1, page_size: pageSize };
    
    let filtered = [...allControls.items];

    // Filter by search term
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter((control) => {
        return (
          control.control_code?.toLowerCase().includes(searchLower) ||
          control.title?.toLowerCase().includes(searchLower)
        );
      });
    }

    // Calculate pagination
    const total = filtered.length;
    const startIndex = (page - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const paginatedItems = filtered.slice(startIndex, endIndex);

    return {
      items: paginatedItems,
      total,
      page,
      page_size: pageSize,
    };
  }, [allControls, searchTerm, page, pageSize]);
  
  // Handle page size change
  const handlePageSizeChange = (event) => {
    const newPageSize = parseInt(event.target.value, 10);
    setPageSize(newPageSize);
    localStorage.setItem("suppression_controls_page_size", newPageSize.toString());
    setPage(1); // Reset to first page when page size changes
  };
  
  // Handle pagination
  const handleChangePage = (newPage) => {
    setPage(newPage);
  };

  // Severity color mapping (from ChecksGrid)
  const SEVERITY_COLORS = {
    CRITICAL: "#b71c1c",
    HIGH: "#f44336",
    MEDIUM: "#fbc02d",
    LOW: "#4caf50",
    INFORMATIONAL: "#1976d2",
    DEFAULT: "#bdbdbd",
  };

  // Handle control checkbox toggle
  const handleControlCheckboxToggle = (controlId, isChecked) => {
    setSelectedControls((prev) => {
      const newSet = new Set(prev);
      if (isChecked) {
        newSet.add(controlId);
      } else {
        newSet.delete(controlId);
        // Also clear selected checks for this control
        setSelectedChecks((prevChecks) => {
          const newChecks = { ...prevChecks };
          delete newChecks[controlId];
          return newChecks;
        });
      }
      return newSet;
    });
  };

  // Handle checks column click
  const handleChecksClick = (control) => {
    // Clear any lingering success/error messages when opening drawer
    dispatch(clearBulkSuppressionMessages());
    
    const controlId = control.control_id;
    
    // Clear all state related to this control when opening drawer
    setSelectedChecks((prev) => {
      const newChecks = { ...prev };
      delete newChecks[controlId];
      return newChecks;
    });
    
    // Remove control from selectedControls (we'll re-add if all checks are selected)
    setSelectedControls((prev) => {
      const newControls = new Set(prev);
      newControls.delete(controlId);
      return newControls;
    });
    
    setSelectedControl(control);
    setChecksDrawerOpen(true);
    setChecksSearchTerm("");
    setChecksSeverity("");
    setChecksService("");
    setChecksPage(0);
    setAllChecks(null); // Reset checks when opening drawer
    setInitialChecksForCurrentControl(new Set()); // Reset initial state for new control
    setJustSavedChecks(false); // Reset save flag
  };

  // Handle check checkbox toggle in drawer
  const handleCheckCheckboxToggle = (checkId, isChecked) => {
    if (!selectedControl) return;
    
    const controlId = selectedControl.control_id;
    setSelectedChecks((prev) => {
      const newChecks = { ...prev };
      if (!newChecks[controlId]) {
        newChecks[controlId] = new Set();
      }
      
      if (isChecked) {
        newChecks[controlId].add(checkId);
      } else {
        newChecks[controlId].delete(checkId);
      }
      
      // Update control selection based on whether all checks are selected
      const allChecksForControl = allChecks?.items || [];
      const allCheckIds = new Set(allChecksForControl.map(check => check.check_id));
      const isAllSelected = allChecksForControl.length > 0 && 
                           newChecks[controlId].size === allCheckIds.size &&
                           Array.from(newChecks[controlId]).every(id => allCheckIds.has(id));
      
      setSelectedControls((prevControls) => {
        const newControls = new Set(prevControls);
        if (isAllSelected) {
          newControls.add(controlId);
        } else {
          newControls.delete(controlId);
        }
        return newControls;
      });
      
      return newChecks;
    });
  };

  // Handle save suppressions for checks (Checks drawer) - Simple logic only
  const handleSaveChecksSuppressions = async () => {
    if (!scheduleId || !frameworkId || !selectedControl) {
      console.error("Cannot save: Missing schedule/framework/control");
      return;
    }

    // Show schedule selection dialog first
    await handleOpenScheduleSelection('checks');
  };

  // Actual save function for checks (called after schedule selection)
  const performSaveChecksSuppressions = async (targetScheduleIds = []) => {
    if (!scheduleId || !frameworkId || !selectedControl) {
      console.error("Cannot save: Missing schedule/framework/control");
      return;
    }

    const controlId = selectedControl.control_id;
    
    // If control is selected, treat all checks as selected
    const isControlSelected = selectedControls.has(controlId);
    const allCheckIds = new Set((allChecks?.items || []).map(check => check.check_id));
    
    // Get current selection: if control is selected, use all checks; otherwise use individual selections
    const currentChecks = isControlSelected ? allCheckIds : (selectedChecks[controlId] || new Set());
    const initialChecks = initialChecksForCurrentControl;

    console.log("=== SAVE CHECKS DEBUG ===");
    console.log("Control selected:", isControlSelected);
    console.log("Initial checks (baseline from API):", Array.from(initialChecks), "count:", initialChecks.size);
    console.log("Current checks (user selection):", Array.from(currentChecks), "count:", currentChecks.size);

    // SIMPLE LOGIC:
    // 1. Delete: Checks that were in initialChecks but are NOT in currentChecks (unselected)
    const deletePayload = [];
    initialChecks.forEach((checkId) => {
      if (!currentChecks.has(checkId)) {
        deletePayload.push({
          complianceaddon_id: parseInt(frameworkId, 10),
          control_id: controlId,
          check_id: checkId,
          scope: "CHECK",
        });
      }
    });

    // 2. Create: ALL currently selected checks (always send all selected, not just difference)
    const createPayload = [];
    currentChecks.forEach((checkId) => {
      createPayload.push({
        complianceaddon_id: parseInt(frameworkId, 10),
        control_id: controlId,
        check_id: checkId,
        scope: "CHECK",
        reason: "Suppressed via UI",
      });
    });

    console.log("Delete payload (unselected checks):", deletePayload.length, "items");
    if (deletePayload.length > 0) {
      console.log("  - IDs:", deletePayload.map(p => p.check_id));
    }
    console.log("Create payload (newly selected checks):", createPayload.length, "items");
    if (createPayload.length > 0) {
      console.log("  - IDs:", createPayload.map(p => p.check_id));
    }

    // If no changes, return
    if (deletePayload.length === 0 && createPayload.length === 0) {
      console.log("No changes to save for checks - state is same as initial");
      return;
    }

    // Open dialog and start save process
    setSaveChecksDialogOpen(true);
    setSaveChecksStatus({
      step: 0,
      deleteMessage: "",
      createMessage: "",
      error: null,
    });

    try {
      // Helper function to wait for API call to complete
      const waitForSaveOperation = () => {
        return new Promise((resolve, reject) => {
          const checkInterval = setInterval(() => {
            const state = store.getState();
            const suppressionState = state.suppression;
            const currentState = {
              isSaving: suppressionState.isSavingSuppressions,
              success: suppressionState.saveSuppressionsSuccess,
              error: suppressionState.saveSuppressionsError,
            };

            if (!currentState.isSaving) {
              clearInterval(checkInterval);
              if (currentState.error) {
                reject(currentState.error);
              } else if (currentState.success) {
                resolve(currentState.success);
              } else {
                setTimeout(() => {
                  const finalState = store.getState().suppression;
                  if (finalState.saveSuppressionsError) {
                    reject(finalState.saveSuppressionsError);
                  } else if (finalState.saveSuppressionsSuccess) {
                    resolve(finalState.saveSuppressionsSuccess);
                  } else {
                    reject(new Error("Operation completed but no result"));
                  }
                }, 200);
              }
            }
          }, 100);

          setTimeout(() => {
            clearInterval(checkInterval);
            reject(new Error("Operation timed out"));
          }, 30000);
        });
      };

      let currentStep = 0;

      // Step 1: Delete unselected checks
      if (deletePayload.length > 0) {
        currentStep++;
        console.log(`Step ${currentStep}: Deleting checks`, deletePayload);
        setSaveChecksStatus((prev) => ({
          ...prev,
          step: currentStep,
          deleteMessage: `Unselecting ${deletePayload.length} check(s)...`,
        }));

        dispatch(clearBulkSuppressionMessages());

        const deleteQueryParams = { delete: true };
        if (targetScheduleIds.length > 0) {
          deleteQueryParams.apply_to = targetScheduleIds.join(',');
        }

        dispatch(bulkUpsertSuppressions({
          schedule_id: scheduleId,
          payload: deletePayload,
          queryParams: deleteQueryParams,
        }));

        await waitForSaveOperation();
        console.log(`Step ${currentStep}: Delete checks completed successfully`);

        setSaveChecksStatus((prev) => ({
          ...prev,
          deleteMessage: `Successfully unselected ${deletePayload.length} check(s)`,
        }));
      }

      // Step 2: Create/Update ALL selected checks (always send all selected, not just difference)
      if (createPayload.length > 0) {
        currentStep++;
        console.log(`Step ${currentStep}: Creating/updating all selected checks`, createPayload);
        setSaveChecksStatus((prev) => ({
          ...prev,
          step: currentStep,
          createMessage: `Suppressing ${createPayload.length} check(s)...`,
        }));

        dispatch(clearBulkSuppressionMessages());

        const createQueryParams = {};
        if (targetScheduleIds.length > 0) {
          createQueryParams.apply_to = targetScheduleIds.join(',');
        }

        dispatch(bulkUpsertSuppressions({
          schedule_id: scheduleId,
          payload: createPayload,
          queryParams: createQueryParams,
        }));

        await waitForSaveOperation();
        console.log(`Step ${currentStep}: Create/update checks completed successfully`);

        setSaveChecksStatus((prev) => ({
          ...prev,
          createMessage: `Successfully suppressed ${createPayload.length} check(s)`,
        }));
      }

      // Final step: Refresh checks data
      const refreshStep = currentStep + 1;
      setSaveChecksStatus((prev) => ({
        ...prev,
        step: refreshStep,
        createMessage: prev.createMessage || "Refreshing checks...",
      }));

      // Mark that we just saved checks (before fetching new data)
      setJustSavedChecks(true);

      // Refresh checks data only (no need to refresh controls)
      const checksParams = {
        schedule_id: scheduleId,
        page: 1,
        page_size: 100,
      };
      
      dispatch(getChecks({ control_id: controlId, params: checksParams }));

      // Wait for checks data to be fetched
      const refreshedChecks = await new Promise((resolve, reject) => {
        const checkInterval = setInterval(() => {
          const state = store.getState();
          const suppressionState = state.suppression;
          
          if (!suppressionState.isFetchingChecks) {
            clearInterval(checkInterval);
            if (suppressionState.fetchChecksError) {
              reject(new Error(suppressionState.fetchChecksError));
            } else if (suppressionState.checks && suppressionState.checks.items) {
              // Data received, wait a bit more for useEffect to process
              setTimeout(() => {
                resolve(suppressionState.checks);
              }, 800); // Increased timeout to ensure useEffect processes
            } else {
              setTimeout(() => {
                const finalState = store.getState().suppression;
                if (finalState.checks && finalState.checks.items) {
                  setTimeout(() => resolve(finalState.checks), 800);
                } else {
                  reject(new Error("Checks data not received"));
                }
              }, 500);
            }
          }
        }, 100);

        setTimeout(() => {
          clearInterval(checkInterval);
          reject(new Error("Refresh timed out"));
        }, 30000);
      });

      // Verify the refreshed data and immediately update initialChecksForCurrentControl
      console.log("=== REFRESHED CHECKS DATA ===");
      console.log("Refreshed checks items:", refreshedChecks.items.length);
      const refreshedSuppressed = new Set();
      refreshedChecks.items.forEach((check) => {
        if (check.is_suppressed) {
          refreshedSuppressed.add(check.check_id);
        }
      });
      console.log("Refreshed suppressed check IDs:", Array.from(refreshedSuppressed), "count:", refreshedSuppressed.size);
      
      // CRITICAL: Immediately update initialChecksForCurrentControl with fresh API data
      // This ensures the next save operation uses the correct baseline
      const newInitialChecks = new Set(refreshedSuppressed);
      console.log("Immediately updating initialChecksForCurrentControl to:", Array.from(newInitialChecks), "count:", newInitialChecks.size);
      setInitialChecksForCurrentControl(newInitialChecks);
      
      // Also update selectedChecks to match API response
      const newSelectedChecks = new Set(refreshedSuppressed);
      setSelectedChecks((prev) => {
        const updated = {
          ...prev,
          [controlId]: newSelectedChecks,
        };
        console.log("Updated selectedChecks for control:", controlId, "to:", Array.from(newSelectedChecks), "count:", newSelectedChecks.size);
        return updated;
      });
      
      // Verify this matches what we expect
      if (createPayload.length > 0 && refreshedSuppressed.size !== createPayload.length) {
        console.warn("WARNING: Expected", createPayload.length, "suppressed checks but got", refreshedSuppressed.size);
      } else {
        console.log("✓ State updated correctly - initialChecksForCurrentControl now has", refreshedSuppressed.size, "checks");
      }

      // Refresh controls data to update suppressed_checks counts
      console.log("Refreshing controls data after checks save...");
      const controlsParams = {
        schedule_id: scheduleId,
        page: 1,
        page_size: API_PAGE_SIZE,
      };
      
      // Reset fetched pages to force fresh data
      setFetchedApiPages(new Set([1]));
      
      dispatch(getControls({ complianceaddon_id: frameworkId, params: controlsParams }));
      
      // Wait for controls data to be fetched (non-blocking, just to ensure it starts)
      // Don't wait for completion as it might take time and we don't want to block the UI
      setTimeout(() => {
        const state = store.getState();
        if (state.suppression.isFetchingControls) {
          console.log("Controls refresh in progress...");
        } else if (state.suppression.controls) {
          console.log("Controls data refreshed successfully");
        }
      }, 500);

      // Done
      setSaveChecksStatus((prev) => ({
        ...prev,
        step: refreshStep + 1,
      }));

      // Close dialog after a short delay
      setTimeout(() => {
        setSaveChecksDialogOpen(false);
        setSaveChecksStatus({
          step: 0,
          deleteMessage: "",
          createMessage: "",
          error: null,
        });
      }, 1500);
    } catch (error) {
      console.error("Error saving checks suppressions:", error);
      setSaveChecksStatus((prev) => ({
        ...prev,
        error: getErrorMessage(error),
      }));
    }
  };

  // Check if checks state has changed from initial state
  const hasChecksStateChanged = useMemo(() => {
    if (!selectedControl) return false;
    
    const controlId = selectedControl.control_id;
    const isControlSelected = selectedControls.has(controlId);
    const allCheckIds = new Set((allChecks?.items || []).map(check => check.check_id));
    
    // If control is selected, treat all checks as selected
    const currentChecks = isControlSelected ? allCheckIds : (selectedChecks[controlId] || new Set());
    const initialChecks = initialChecksForCurrentControl;

    // Compare sizes
    if (currentChecks.size !== initialChecks.size) {
      return true;
    }

    // Compare contents
    for (const checkId of currentChecks) {
      if (!initialChecks.has(checkId)) {
        return true;
      }
    }

    for (const checkId of initialChecks) {
      if (!currentChecks.has(checkId)) {
        return true;
      }
    }

    return false;
  }, [selectedControl, selectedChecks, selectedControls, initialChecksForCurrentControl, allChecks]);

  // Handle reset all suppressions (Controls page only)
  const handleResetSuppressions = async () => {
    if (!scheduleId || !frameworkId) {
      console.error("Cannot reset: Missing schedule/framework");
      return;
    }

    // Close confirmation dialog and open progress dialog
    setResetConfirmDialogOpen(false);
    setSaveDialogOpen(true);
    setSaveStatus({
      step: 0,
      deleteMessage: "",
      createMessage: "",
      error: null,
    });

    try {
      // Helper function to wait for API call to complete
      const waitForSaveOperation = () => {
        return new Promise((resolve, reject) => {
          const checkInterval = setInterval(() => {
            const state = store.getState();
            const suppressionState = state.suppression;
            const currentState = {
              isSaving: suppressionState.isSavingSuppressions,
              success: suppressionState.saveSuppressionsSuccess,
              error: suppressionState.saveSuppressionsError,
            };

            if (!currentState.isSaving) {
              clearInterval(checkInterval);
              if (currentState.error) {
                reject(currentState.error);
              } else if (currentState.success) {
                resolve(currentState.success);
              } else {
                setTimeout(() => {
                  const finalState = store.getState().suppression;
                  if (finalState.saveSuppressionsError) {
                    reject(finalState.saveSuppressionsError);
                  } else if (finalState.saveSuppressionsSuccess) {
                    resolve(finalState.saveSuppressionsSuccess);
                  } else {
                    reject(new Error("Operation completed but no result"));
                  }
                }, 200);
              }
            }
          }, 100);

          setTimeout(() => {
            clearInterval(checkInterval);
            reject(new Error("Operation timed out"));
          }, 30000);
        });
      };

      // Step 1: Reset all suppressions
      setSaveStatus((prev) => ({
        ...prev,
        step: 1,
        deleteMessage: "Resetting all suppressions...",
      }));

      dispatch(clearBulkSuppressionMessages());

      // Call API with reset=true, empty payload, and framework_id
      dispatch(bulkUpsertSuppressions({
        schedule_id: scheduleId,
        payload: [],
        queryParams: { 
          reset: true,
          framework_id: frameworkId,
        },
      }));

      await waitForSaveOperation();
      console.log("Reset completed successfully");

      setSaveStatus((prev) => ({
        ...prev,
        deleteMessage: "Successfully reset all suppressions",
      }));

      // Step 2: Refresh controls data
      setSaveStatus((prev) => ({
        ...prev,
        step: 2,
        createMessage: "Refreshing controls...",
      }));

      // Mark that we just saved (to trigger state reinitialization when data arrives)
      setJustSaved(true);

      // Reset allControls to force fresh data fetch
      setAllControls(null);
      setFetchedApiPages(new Set([1]));

      // Unselect all controls in UI immediately
      setSelectedControls(new Set());
      setSelectedChecks({});
      setInitialSelectedControls(new Set());
      setInitialSelectedChecks({});

      const params = {
        schedule_id: scheduleId,
        page: 1,
        page_size: API_PAGE_SIZE,
      };
      
      dispatch(getControls({ complianceaddon_id: frameworkId, params }));

      // Wait for controls data to be fetched and processed
      await new Promise((resolve, reject) => {
        const checkInterval = setInterval(() => {
          const state = store.getState();
          const suppressionState = state.suppression;
          
          if (!suppressionState.isFetchingControls) {
            clearInterval(checkInterval);
            if (suppressionState.fetchControlsError) {
              reject(new Error(suppressionState.fetchControlsError));
            } else if (suppressionState.controls && suppressionState.controls.items) {
              setTimeout(() => {
                resolve(suppressionState.controls);
              }, 300);
            } else {
              setTimeout(() => {
                const finalState = store.getState().suppression;
                if (finalState.controls && finalState.controls.items) {
                  setTimeout(() => resolve(finalState.controls), 300);
                } else {
                  reject(new Error("Controls data not received"));
                }
              }, 500);
            }
          }
        }, 100);

        setTimeout(() => {
          clearInterval(checkInterval);
          reject(new Error("Refresh timed out"));
        }, 30000);
      });

      // Step 3: Done
      setSaveStatus((prev) => ({
        ...prev,
        step: 3,
      }));

      // Close dialog after a short delay
      setTimeout(() => {
        setSaveDialogOpen(false);
        setSaveStatus({
          step: 0,
          deleteMessage: "",
          createMessage: "",
          error: null,
        });
      }, 1500);
    } catch (error) {
      console.error("Error resetting suppressions:", error);
      setSaveStatus((prev) => ({
        ...prev,
        error: getErrorMessage(error),
      }));
    }
  };

  // Fetch schedules for schedule selection dialog
  const fetchSchedulesForSelection = async () => {
    if (!selectedSchedule?.account_number && !selectedSchedule?.account_id) {
      console.error("Cannot fetch schedules: Missing account information");
      return;
    }

    setIsFetchingSchedules(true);
    try {
      // Use account_number and send it as account_id in the API params
      const accountNumber = selectedSchedule.account_number || selectedSchedule.account_id;
      if (!accountNumber) {
        console.error("Cannot fetch schedules: Missing account number");
        setAvailableSchedules([]);
        setIsFetchingSchedules(false);
        return;
      }

      const params = {
        page: 1,
        page_size: 100, // Fetch all schedules
        account_id: accountNumber, // Send account_number as account_id
      };

      dispatch(getSchedules(params));

      // Wait for schedules to be fetched
      await new Promise((resolve, reject) => {
        const checkInterval = setInterval(() => {
          const state = store.getState();
          const suppressionState = state.suppression;
          
          if (!suppressionState.isFetchingSchedules) {
            clearInterval(checkInterval);
            if (suppressionState.fetchSchedulesError) {
              reject(new Error(suppressionState.fetchSchedulesError));
            } else if (suppressionState.schedules && suppressionState.schedules.items) {
              // Exclude current schedule
              // Also exclude "Well Architected Framework Review" unless framework is CIS-5.0
              const isCIS50 = selectedFramework?.name === "CIS-5.0";
              const filteredSchedules = suppressionState.schedules.items.filter(
                (schedule) => {
                  if (schedule.schedule_id === scheduleId) {
                    return false; // Exclude current schedule
                  }
                  
                  const scheduleName = formatScheduleName(schedule);
                  // If framework is CIS-5.0, include all schedules (including WAFR)
                  // Otherwise, exclude "Well Architected Framework Review"
                  if (!isCIS50 && scheduleName === "Well Architected Framework Review") {
                    return false;
                  }
                  
                  return true;
                }
              );
              setAvailableSchedules(filteredSchedules);
              resolve(filteredSchedules);
            } else {
              // If no items, might be empty result
              setAvailableSchedules([]);
              resolve([]);
            }
          }
        }, 100);

        setTimeout(() => {
          clearInterval(checkInterval);
          reject(new Error("Fetch schedules timed out"));
        }, 30000);
      });
    } catch (error) {
      console.error("Error fetching schedules:", error);
      setAvailableSchedules([]);
    } finally {
      setIsFetchingSchedules(false);
    }
  };

  // Handle opening schedule selection dialog
  const handleOpenScheduleSelection = async (saveType) => {
    setPendingSaveAction(saveType);
    setSelectedSchedulesToApply(new Set());
    setScheduleSelectionDialogOpen(true);
    await fetchSchedulesForSelection();
  };

  // Handle save suppressions (Controls page only)
  const handleSaveSuppressions = async () => {
    if (!scheduleId || !frameworkId) {
      console.error("Cannot save: Missing schedule/framework");
      return;
    }

    // Show schedule selection dialog first
    await handleOpenScheduleSelection('controls');
  };

  // Actual save function for controls (called after schedule selection)
  const performSaveSuppressions = async (targetScheduleIds = []) => {
    if (!scheduleId || !frameworkId) {
      console.error("Cannot save: Missing schedule/framework");
      return;
    }

    // Find unselected controls (were initially selected, now not)
    const deletePayload = [];
    initialSelectedControls.forEach((controlId) => {
      if (!selectedControls.has(controlId)) {
        const control = allControls?.items?.find((c) => c.control_id === controlId);
        if (control) {
          deletePayload.push({
            complianceaddon_id: parseInt(frameworkId, 10),
            control_id: controlId,
            scope: "CONTROL",
          });
        }
      }
    });

    // Find selected controls (are now selected, weren't initially)
    // IMPORTANT: Only add controls that are NOT in initialSelectedControls (newly selected)
    const createPayload = [];
    selectedControls.forEach((controlId) => {
      if (!initialSelectedControls.has(controlId)) {
        const control = allControls?.items?.find((c) => c.control_id === controlId);
        if (control) {
          createPayload.push({
            complianceaddon_id: parseInt(frameworkId, 10),
            control_id: controlId,
            scope: "CONTROL",
            reason: "Suppressed via UI",
          });
        }
      }
    });

    console.log("Controls Save - Initial controls:", Array.from(initialSelectedControls));
    console.log("Controls Save - Current controls:", Array.from(selectedControls));
    console.log("Controls Save - Delete payload (unselected):", deletePayload);
    console.log("Controls Save - Create payload (newly selected):", createPayload);

    // If no changes, return
    if (deletePayload.length === 0 && createPayload.length === 0) {
      console.log("No changes to save");
      return;
    }

    // Open dialog and start save process
    setSaveDialogOpen(true);
    setSaveStatus({
      step: 0,
      deleteMessage: "",
      createMessage: "",
      error: null,
    });

    try {
      // Helper function to wait for API call to complete
      const waitForSaveOperation = () => {
        return new Promise((resolve, reject) => {
          const checkInterval = setInterval(() => {
            // Access store state directly to get current values
            const state = store.getState();
            const suppressionState = state.suppression;
            const currentState = {
              isSaving: suppressionState.isSavingSuppressions,
              success: suppressionState.saveSuppressionsSuccess,
              error: suppressionState.saveSuppressionsError,
            };

            if (!currentState.isSaving) {
              clearInterval(checkInterval);
              if (currentState.error) {
                reject(currentState.error);
              } else if (currentState.success) {
                resolve(currentState.success);
              } else {
                // If neither error nor success, might be a race condition, wait a bit more
                setTimeout(() => {
                  const finalState = store.getState().suppression;
                  if (finalState.saveSuppressionsError) {
                    reject(finalState.saveSuppressionsError);
                  } else if (finalState.saveSuppressionsSuccess) {
                    resolve(finalState.saveSuppressionsSuccess);
                  } else {
                    reject(new Error("Operation completed but no result"));
                  }
                }, 200);
              }
            }
          }, 100);

          // Timeout after 30 seconds
          setTimeout(() => {
            clearInterval(checkInterval);
            reject(new Error("Operation timed out"));
          }, 30000);
        });
      };

      // Step 1: Delete unselected controls
      if (deletePayload.length > 0) {
        console.log("Step 1: Deleting controls", deletePayload);
        setSaveStatus((prev) => ({
          ...prev,
          step: 1,
          deleteMessage: `Unselecting ${deletePayload.length} control(s)...`,
        }));

        // Clear previous messages
        dispatch(clearBulkSuppressionMessages());

        const deleteQueryParams = { delete: true };
        if (targetScheduleIds.length > 0) {
          deleteQueryParams.apply_to = targetScheduleIds.join(',');
        }

        dispatch(bulkUpsertSuppressions({
          schedule_id: scheduleId,
          payload: deletePayload,
          queryParams: deleteQueryParams,
        }));

        await waitForSaveOperation();
        console.log("Step 1: Delete completed successfully");

        setSaveStatus((prev) => ({
          ...prev,
          deleteMessage: `Successfully unselected ${deletePayload.length} control(s)`,
        }));
      }

      // Step 2: Create selected controls (only after delete succeeds)
      if (createPayload.length > 0) {
        console.log("Step 2: Creating controls", createPayload);
        setSaveStatus((prev) => ({
          ...prev,
          step: 2,
          createMessage: `Suppressing ${createPayload.length} control(s)...`,
        }));

        // Clear previous messages
        dispatch(clearBulkSuppressionMessages());

        const createQueryParams = {};
        if (targetScheduleIds.length > 0) {
          createQueryParams.apply_to = targetScheduleIds.join(',');
        }

        dispatch(bulkUpsertSuppressions({
          schedule_id: scheduleId,
          payload: createPayload,
          queryParams: createQueryParams,
        }));

        await waitForSaveOperation();
        console.log("Step 2: Create completed successfully");

        setSaveStatus((prev) => ({
          ...prev,
          createMessage: `Successfully suppressed ${createPayload.length} control(s)`,
        }));
      }

      // Step 3: Refresh controls data
      setSaveStatus((prev) => ({
        ...prev,
        step: 3,
        createMessage: prev.createMessage || "Refreshing controls...",
      }));

      // Mark that we just saved (to trigger state reinitialization when data arrives)
      setJustSaved(true);

      // Reset allControls to force fresh data fetch
      setAllControls(null);
      setFetchedApiPages(new Set([1]));

      const params = {
        schedule_id: scheduleId,
        page: 1,
        page_size: API_PAGE_SIZE,
      };
      
      dispatch(getControls({ complianceaddon_id: frameworkId, params }));

      // Wait for controls data to be fetched and processed
      await new Promise((resolve, reject) => {
        const checkInterval = setInterval(() => {
          const state = store.getState();
          const suppressionState = state.suppression;
          
          if (!suppressionState.isFetchingControls) {
            clearInterval(checkInterval);
            if (suppressionState.fetchControlsError) {
              reject(new Error(suppressionState.fetchControlsError));
            } else if (suppressionState.controls && suppressionState.controls.items) {
              // Wait a bit more for useEffect to process the data
              setTimeout(() => {
                resolve(suppressionState.controls);
              }, 300);
            } else {
              // Wait a bit more for data to arrive
              setTimeout(() => {
                const finalState = store.getState().suppression;
                if (finalState.controls && finalState.controls.items) {
                  setTimeout(() => resolve(finalState.controls), 300);
                } else {
                  reject(new Error("Controls data not received"));
                }
              }, 500);
            }
          }
        }, 100);

        // Timeout after 30 seconds
        setTimeout(() => {
          clearInterval(checkInterval);
          reject(new Error("Refresh timed out"));
        }, 30000);
      });

      // Step 4: Done
      setSaveStatus((prev) => ({
        ...prev,
        step: 4,
      }));

      // Close dialog after a short delay
      setTimeout(() => {
        setSaveDialogOpen(false);
        setSaveStatus({
          step: 0,
          deleteMessage: "",
          createMessage: "",
          error: null,
        });
      }, 1500);
    } catch (error) {
      console.error("Error saving suppressions:", error);
      setSaveStatus((prev) => ({
        ...prev,
        error: getErrorMessage(error),
      }));
    }
  };

  // Check if state has changed from initial state
  const hasStateChanged = useMemo(() => {
    // Compare selectedControls
    if (selectedControls.size !== initialSelectedControls.size) {
      return true;
    }
    for (const controlId of selectedControls) {
      if (!initialSelectedControls.has(controlId)) {
        return true;
      }
    }
    for (const controlId of initialSelectedControls) {
      if (!selectedControls.has(controlId)) {
        return true;
      }
    }
    
    // Compare selectedChecks
    const currentCheckKeys = Object.keys(selectedChecks);
    const initialCheckKeys = Object.keys(initialSelectedChecks);
    
    if (currentCheckKeys.length !== initialCheckKeys.length) {
      return true;
    }
    
    for (const controlId of currentCheckKeys) {
      const currentChecks = selectedChecks[controlId];
      const initialChecks = initialSelectedChecks[controlId];
      
      if (!initialChecks) {
        if (currentChecks && currentChecks.size > 0) {
          return true;
        }
        continue;
      }
      
      if (currentChecks.size !== initialChecks.size) {
        return true;
      }
      
      for (const checkId of currentChecks) {
        if (!initialChecks.has(checkId)) {
          return true;
        }
      }
    }
    
    for (const controlId of initialCheckKeys) {
      const initialChecks = initialSelectedChecks[controlId];
      const currentChecks = selectedChecks[controlId];
      
      if (!currentChecks || currentChecks.size === 0) {
        if (initialChecks && initialChecks.size > 0) {
          return true;
        }
        continue;
      }
    }
    
    return false;
  }, [selectedControls, selectedChecks, initialSelectedControls, initialSelectedChecks]);

  // Fetch checks only when drawer opens (once)
  useEffect(() => {
    if (checksDrawerOpen && selectedControl && scheduleId && !allChecks) {
      const params = {
        schedule_id: scheduleId,
        page: 1,
        page_size: 100, // Fetch checks for client-side filtering
      };

      dispatch(getChecks({ control_id: selectedControl.control_id, params }));
    }
  }, [dispatch, checksDrawerOpen, selectedControl, scheduleId, allChecks]);


  // Handle save success/error messages
  // Only show snackbar if we're not showing a dialog (dialog takes precedence)
  useEffect(() => {
    if (saveSuppressionsSuccess && !saveDialogOpen && !saveChecksDialogOpen) {
      const message = saveSuppressionsSuccess.message || "Suppressions processed";
      setSnackbar({
        open: true,
        message: message,
        severity: "success",
      });
      // Clear the success message after showing snackbar
      setTimeout(() => {
        dispatch(clearBulkSuppressionMessages());
      }, 100);
    }
    if (saveSuppressionsError && !saveDialogOpen && !saveChecksDialogOpen) {
      setSnackbar({
        open: true,
        message: getErrorMessage(saveSuppressionsError),
        severity: "error",
      });
      // Clear the error message after showing snackbar
      setTimeout(() => {
        dispatch(clearBulkSuppressionMessages());
      }, 100);
    }
  }, [saveSuppressionsSuccess, saveSuppressionsError, dispatch, saveDialogOpen, saveChecksDialogOpen]);

  // Track if we just saved checks (to reinitialize state after refresh)
  const [justSavedChecks, setJustSavedChecks] = useState(false);

  // Store checks when they're fetched
  useEffect(() => {
    if (checks && checks.items && selectedControl && checksDrawerOpen) {
      console.log("Checks useEffect triggered - checks data:", checks.items.length, "items");
      console.log("Checks useEffect - justSavedChecks:", justSavedChecks);
      
      const controlId = selectedControl.control_id;
      
      // Only process checks if drawer is open for this control
      // This prevents stale data from previous controls from affecting the UI
      
      // Always update allChecks with fresh data (create new object to ensure re-render)
      // Deep copy to ensure React detects the change
      const newChecksData = {
        ...checks,
        items: checks.items.map(item => ({ ...item }))
      };
      setAllChecks(newChecksData);
      console.log("Updated allChecks with", newChecksData.items.length, "items");
      
      // Initialize check-level suppressions in initial state and selected state
      const suppressedCheckIds = new Set();
      checks.items.forEach((check) => {
        if (check.is_suppressed) {
          suppressedCheckIds.add(check.check_id);
        }
      });
      
      console.log("Checks useEffect - suppressedCheckIds:", Array.from(suppressedCheckIds));
      console.log("Checks useEffect - allChecks items is_suppressed:", 
        newChecksData.items.map(c => ({ id: c.check_id, suppressed: c.is_suppressed })));
      
      // If we just saved checks, state was already updated in save function
      // Just reset the flag - don't update again to avoid conflicts
      if (justSavedChecks) {
        console.log("useEffect triggered after save - state already updated in save function");
        setJustSavedChecks(false);
      } else {
        // Always reinitialize state from fresh API data when checks are loaded
        // This ensures state matches the actual API response and fixes stale state issues
        console.log("Initializing checks state from API data");
        setInitialChecksForCurrentControl(new Set(suppressedCheckIds));
        
        // Set selected state to match initial state (only suppressed checks are selected)
        // Always create a new Set to ensure React detects the change
        setSelectedChecks((prev) => {
          const updated = {
            ...prev,
            [controlId]: new Set(suppressedCheckIds),
          };
          console.log("Updated selectedChecks for control:", controlId, "to:", Array.from(suppressedCheckIds));
          return updated;
        });
        
        // Update control selection: if all checks are suppressed, select control; otherwise deselect
        setSelectedControls((prev) => {
          const newControls = new Set(prev);
          const allCheckIds = new Set(checks.items.map(check => check.check_id));
          const isAllSelected = allCheckIds.size > 0 && 
                               suppressedCheckIds.size === allCheckIds.size &&
                               Array.from(suppressedCheckIds).every(id => allCheckIds.has(id));
          
          if (isAllSelected) {
            newControls.add(controlId);
          } else {
            newControls.delete(controlId);
          }
          return newControls;
        });
      }
    }
  }, [checks, selectedControl, justSavedChecks, checksDrawerOpen]);

  // Client-side filtering
  const filteredChecks = useMemo(() => {
    if (!allChecks || !allChecks.items) return { items: [], total: 0 };

    let filtered = allChecks.items;

    // Filter by search term
    if (checksSearchTerm) {
      const searchLower = checksSearchTerm.toLowerCase();
      filtered = filtered.filter(
        (check) =>
          check.title?.toLowerCase().includes(searchLower) ||
          check.check_id?.toLowerCase().includes(searchLower)
      );
    }

    // Filter by severity
    if (checksSeverity) {
      filtered = filtered.filter(
        (check) => check.severity?.toUpperCase() === checksSeverity.toUpperCase()
      );
    }

    // Filter by service
    if (checksService) {
      filtered = filtered.filter(
        (check) => check.service?.toLowerCase() === checksService.toLowerCase()
      );
    }

    return {
      items: filtered,
      total: filtered.length,
    };
  }, [allChecks, checksSearchTerm, checksSeverity, checksService]);

  // Paginated checks
  const paginatedChecks = useMemo(() => {
    const start = checksPage * checksPageSize;
    const end = start + checksPageSize;
    return filteredChecks.items.slice(start, end);
  }, [filteredChecks, checksPage, checksPageSize]);

  // Get unique services for filter dropdown
  const availableServices = useMemo(() => {
    if (!allChecks || !allChecks.items) return [];
    const services = new Set();
    allChecks.items.forEach((check) => {
      if (check.service) {
        services.add(check.service);
      }
    });
    return Array.from(services).sort();
  }, [allChecks]);

  // Handle checks drawer close
  const handleChecksDrawerClose = () => {
    // Clean up all drawer-related state
    setChecksDrawerOpen(false);
    setSelectedControl(null);
    setChecksSearchTerm("");
    setChecksSeverity("");
    setChecksService("");
    setChecksPage(0);
    setAllChecks(null);
    setInitialChecksForCurrentControl(new Set());
    setJustSavedChecks(false);
    // Note: We don't clear selectedChecks or selectedControls here because
    // those are used for the main controls page and should persist
  };

  // Handle pagination
  const handleChecksPageChange = (newPage) => {
    setChecksPage(newPage);
  };

  // Helper function to safely extract error message
  const getErrorMessage = (error) => {
    if (!error) return "An unexpected error occurred";
    if (typeof error === "string") return error;
    if (typeof error === "object") {
      if (error.error && typeof error.error === "string") return error.error;
      if (error.message && typeof error.message === "string")
        return error.message;
      if (error.error && typeof error.error === "object") {
        if (error.error.message) return error.error.message;
        if (error.error.error) return error.error.error;
      }
      try {
        const stringified = JSON.stringify(error);
        if (stringified.length > 200) {
          return "An error occurred. Please check the console for details.";
        }
        return stringified;
      } catch {
        return "An unexpected error occurred";
      }
    }
    return String(error);
  };

  // Get pagination info
  const totalPages = filteredControls?.total
    ? Math.ceil(filteredControls.total / pageSize)
    : 0;
  const currentPageStart = filteredControls?.items?.length
    ? (page - 1) * pageSize + 1
    : 0;
  const currentPageEnd = filteredControls?.items?.length
    ? Math.min(page * pageSize, filteredControls.total || 0)
    : 0;

  return (
    <Box
      sx={{
        padding: "1.5rem 2rem",
        mt: "4rem",
        mb: "3rem",
        backgroundColor: "#F9F9F9",
        minHeight: "100vh",
        fontFamily:
          '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      }}
    >
      {/* Breadcrumbs */}
      <Box sx={{ mb: 2.5 }}>
        <Breadcrumbs
          separator={
            <Box
              component="span"
              sx={{
                color: "#cbd5e1",
                fontSize: "0.875rem",
                mx: 0.5,
                display: "flex",
                alignItems: "center",
              }}
            >
              /
            </Box>
          }
          aria-label="breadcrumb"
          sx={{
            "& .MuiBreadcrumbs-ol": {
              flexWrap: "nowrap",
            },
          }}
        >
          <Link
            component="button"
            variant="body2"
            onClick={() => handleBreadcrumbClick("/configuration/suppression")}
            sx={{
              color: "#64748b",
              textDecoration: "none",
              fontSize: "0.875rem",
              fontWeight: 400,
              transition: "color 0.2s ease",
              "&:hover": {
                color: "#1e293b",
              },
              cursor: "pointer",
              px: 0.5,
            }}
          >
            Schedules
          </Link>
          <Link
            component="button"
            variant="body2"
            onClick={() => handleBreadcrumbClick(`/configuration/suppression/frameworks/${scheduleId}`)}
            sx={{
              color: "#64748b",
              textDecoration: "none",
              fontSize: "0.875rem",
              fontWeight: 400,
              transition: "color 0.2s ease",
              "&:hover": {
                color: "#1e293b",
              },
              cursor: "pointer",
              px: 0.5,
            }}
          >
            Frameworks
          </Link>
          <Typography
            variant="body2"
            sx={{
              color: "#1e293b",
              fontSize: "0.875rem",
              fontWeight: 500,
              px: 0.5,
            }}
          >
            Controls
          </Typography>
        </Breadcrumbs>
      </Box>

      {/* Header with Back Button on Right */}
      <Box sx={{ 
        mb: 2.5, 
        display: "flex", 
        alignItems: "center", 
        justifyContent: "space-between",
        borderBottom: "1px solid #e5e7eb",
        pb: 1.5
      }}>
        <Box>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              color: "#374151",
              fontSize: "1rem",
              letterSpacing: "-0.01em",
              mb: 0.25,
            }}
          >
            Controls
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: "#6b7280",
              fontSize: "0.8125rem",
              fontWeight: 400,
            }}
          >
            {selectedFramework && selectedSchedule
              ? `Framework: ${selectedFramework.name || "N/A"} | Schedule: ${formatScheduleName(selectedSchedule)} | Account: ${selectedSchedule.account_number || selectedSchedule.account_id || "N/A"}`
              : "Viewing controls for selected framework"}
          </Typography>
        </Box>
        <Button
          onClick={handleBack}
          startIcon={<ArrowBackIcon sx={{ fontSize: 18 }} />}
          sx={{
            color: "#64748b",
            backgroundColor: "transparent",
            border: "1px solid #e2e8f0",
            borderRadius: "6px",
            textTransform: "none",
            fontSize: "0.875rem",
            fontWeight: 500,
            px: 2,
            py: 1,
            "&:hover": {
              backgroundColor: "#f8fafc",
              color: "#374151",
              borderColor: "#d1d5db",
            },
            transition: "all 0.2s ease-in-out",
          }}
        >
          Back
        </Button>
      </Box>

      {/* Loading State */}
      {isFetchingControls && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            py: 8,
          }}
        >
          <CircularProgress size={40} sx={{ color: "#3b82f6" }} />
          <Typography
            variant="body2"
            sx={{
              ml: 2,
              color: "#64748b",
              fontSize: "0.875rem",
              fontWeight: 500,
            }}
          >
            Loading controls...
          </Typography>
        </Box>
      )}

      {/* Error State */}
      {fetchControlsError && !isFetchingControls && (
        <Box
          sx={{
            mb: 4,
            p: 3,
            backgroundColor: "#fef2f2",
            borderRadius: "8px",
            border: "1px solid #f87171",
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: "#991b1b",
              fontSize: "0.875rem",
              fontWeight: 500,
            }}
          >
            {getErrorMessage(fetchControlsError)}
          </Typography>
        </Box>
      )}

      {/* Controls Table */}
      {allControls && !isFetchingControls && !fetchControlsError && (
        <Card
          sx={{
            borderRadius: "8px",
            border: "1px solid #e2e8f0",
            boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
          }}
        >
          <CardContent sx={{ p: 0 }}>
            <Box
              sx={{
                p: 2,
                borderBottom: "1px solid #e5e7eb",
                backgroundColor: "#ffffff",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  color: "#1e293b",
                  fontSize: "0.875rem",
                }}
              >
                Controls
              </Typography>
              <Box sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
                <TextField
                size="small"
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setPage(1); // Reset to first page when searching
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ fontSize: 18, color: "#64748b" }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  width: 400,
                  "& .MuiOutlinedInput-root": {
                    fontSize: "0.875rem",
                    backgroundColor: "white",
                    borderRadius: "6px",
                    "& fieldset": {
                      borderColor: "#e2e8f0",
                      borderWidth: "1px",
                    },
                    "&:hover fieldset": {
                      borderColor: "#d1d5db",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#3b82f6",
                      borderWidth: "2px",
                    },
                  },
                  "& .MuiInputBase-input": {
                    padding: "8px 12px",
                  },
                }}
              />
                <Box sx={{ display: "flex", gap: 1 }}>
                  <Button
                    variant="outlined"
                    startIcon={<SaveIcon sx={{ fontSize: 18 }} />}
                    onClick={handleSaveSuppressions}
                    disabled={isSavingSuppressions || !hasStateChanged}
                      sx={{
                        color: "#374151",
                        borderColor: "#e2e8f0",
                        textTransform: "none",
                        fontSize: "0.875rem",
                        fontWeight: 500,
                        px: 2,
                        py: 0.75,
                        borderRadius: "6px",
                        backgroundColor: "transparent",
                        "&:hover": {
                          backgroundColor: "#f8fafc",
                          borderColor: "#d1d5db",
                          color: "#1e293b",
                        },
                        "&:disabled": {
                          backgroundColor: "transparent",
                          borderColor: "#e5e7eb",
                          color: "#9ca3af",
                        },
                      }}
                    >
                      Save
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<RestartAltIcon sx={{ fontSize: 18 }} />}
                      onClick={() => setResetConfirmDialogOpen(true)}
                      disabled={isSavingSuppressions}
                      sx={{
                        color: "#374151",
                        borderColor: "#e2e8f0",
                        textTransform: "none",
                        fontSize: "0.875rem",
                        fontWeight: 500,
                        px: 2,
                        py: 0.75,
                        borderRadius: "6px",
                        backgroundColor: "transparent",
                        "&:hover": {
                          backgroundColor: "#f8fafc",
                          borderColor: "#d1d5db",
                          color: "#1e293b",
                        },
                        "&:disabled": {
                          backgroundColor: "transparent",
                          borderColor: "#e5e7eb",
                          color: "#9ca3af",
                        },
                      }}
                    >
                      Reset
                    </Button>
                  </Box>
              </Box>
            </Box>

            <TableContainer
              sx={{
                overflowX: "auto",
                "&::-webkit-scrollbar": {
                  height: "8px",
                },
                "&::-webkit-scrollbar-track": {
                  backgroundColor: "#f1f5f9",
                },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: "#cbd5e1",
                  borderRadius: "4px",
                  "&:hover": {
                    backgroundColor: "#94a3b8",
                  },
                },
              }}
            >
              <Table size="small" sx={{ minWidth: 800 }}>
                <TableHead>
                  <TableRow sx={{ backgroundColor: "#fafafa" }}>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        color: "#374151",
                        fontSize: "0.75rem",
                        py: 0.25,
                        width: 50,
                      }}
                    >
                      <Checkbox
                        indeterminate={
                          selectedControls.size > 0 && selectedControls.size < filteredControls.items.length
                        }
                        checked={
                          filteredControls.items.length > 0 && selectedControls.size === filteredControls.items.length
                        }
                        onChange={(e) => {
                          if (e.target.checked) {
                            const allControlIds = new Set(filteredControls.items.map((c) => c.control_id));
                            setSelectedControls(allControlIds);
                          } else {
                            setSelectedControls(new Set());
                          }
                        }}
                        size="small"
                      />
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        color: "#374151",
                        fontSize: "0.75rem",
                        py: 0.25,
                      }}
                    >
                      Control Id
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        color: "#374151",
                        fontSize: "0.75rem",
                        py: 0.25,
                      }}
                    >
                      Title
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        color: "#374151",
                        fontSize: "0.75rem",
                        py: 0.25,
                      }}
                    >
                      Suppression Status
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        color: "#374151",
                        fontSize: "0.75rem",
                        py: 0.25,
                      }}
                    >
                      Checks
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredControls?.items && filteredControls.items.length > 0 ? (
                    filteredControls.items.map((control, index) => (
                      <TableRow
                        key={control.control_id || index}
                        sx={{
                          "&:hover": {
                            backgroundColor: "#f8fafc",
                            "& td": {
                              backgroundColor: "#f8fafc",
                            },
                          },
                        }}
                      >
                        <TableCell sx={{ py: 0.25 }}>
                          <Checkbox
                            checked={selectedControls.has(control.control_id)}
                            onChange={(e) => handleControlCheckboxToggle(control.control_id, e.target.checked)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell sx={{ py: 0.25 }}>
                          <Typography
                            variant="body2"
                            sx={{
                              color: "#374151",
                              fontSize: "0.75rem",
                              fontWeight: 600,
                              lineHeight: 1.3,
                            }}
                          >
                            {control.control_code || "N/A"}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ py: 0.25 }}>
                          <Typography
                            variant="body2"
                            sx={{
                              color: "#374151",
                              fontSize: "0.75rem",
                              lineHeight: 1.3,
                            }}
                          >
                            {control.title || "N/A"}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ py: 0.25 }}>
                          {(() => {
                            // Control-level suppression (all checks suppressed)
                            if (control.is_suppressed && !control.check_id) {
                              return (
                                <Typography
                                  variant="body2"
                                  sx={{
                                    color: "#d97706",
                                    fontSize: "0.75rem",
                                    fontWeight: 600,
                                    lineHeight: 1.3,
                                  }}
                                >
                                  Suppressed
                                </Typography>
                              );
                            }
                            
                            // Check-level suppression logic
                            const suppressedCount = control.suppressed_checks !== undefined ? control.suppressed_checks : 0;
                            const totalCount = control.total_checks !== undefined ? control.total_checks : 0;
                            
                            let statusText = "Not Suppressed";
                            let statusColor = "#059669";
                            
                            if (totalCount === 0) {
                              statusText = "Not Suppressed";
                              statusColor = "#059669";
                            } else if (suppressedCount === 0) {
                              statusText = "Not Suppressed";
                              statusColor = "#059669";
                            } else if (suppressedCount > 0 && suppressedCount < totalCount) {
                              statusText = "Partially Suppressed";
                              statusColor = "#f59e0b";
                            } else if (suppressedCount === totalCount) {
                              statusText = "Suppressed";
                              statusColor = "#d97706";
                            }
                            
                            return (
                              <Typography
                                variant="body2"
                                sx={{
                                  color: statusColor,
                                  fontSize: "0.75rem",
                                  fontWeight: 600,
                                  lineHeight: 1.3,
                                }}
                              >
                                {statusText}
                              </Typography>
                            );
                          })()}
                        </TableCell>
                        <TableCell sx={{ py: 0.25 }}>
                          <Tooltip title={control.total_checks > 0 ? `Click to view ${control.total_checks} ${control.total_checks === 1 ? 'check' : 'checks'}` : "No checks available"}>
                            <Chip
                              label={control.total_checks !== undefined
                                ? (control.is_suppressed && !control.check_id
                                    ? `${control.total_checks}/${control.total_checks}`
                                    : control.suppressed_checks !== undefined
                                      ? `${control.suppressed_checks}/${control.total_checks}`
                                      : `0/${control.total_checks}`)
                                : "N/A"}
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (control.total_checks > 0) {
                                  handleChecksClick(control);
                                }
                              }}
                              sx={{
                                backgroundColor: control.total_checks > 0 ? "#f3f4f6" : "#f5f5f5",
                                color: control.total_checks > 0 ? "#374151" : "#9e9e9e",
                                cursor: control.total_checks > 0 ? "pointer" : "not-allowed",
                                opacity: control.total_checks > 0 ? 1 : 0.6,
                                fontSize: "0.7rem",
                                fontWeight: 600,
                                height: 20,
                                "& .MuiChip-label": {
                                  padding: "0 8px",
                                },
                                "&:hover": {
                                  backgroundColor: control.total_checks > 0 ? "#e5e7eb" : "#f5f5f5",
                                  color: control.total_checks > 0 ? "#1e293b" : "#9e9e9e",
                                },
                                transition: "all 0.2s ease-in-out",
                              }}
                            />
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={5} sx={{ py: 4, textAlign: "center" }}>
                        <Typography
                          variant="body2"
                          sx={{
                            color: "#64748b",
                            fontSize: "0.875rem",
                          }}
                        >
                          No controls found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Pagination */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                px: 2,
                py: 1.5,
                borderTop: "1px solid #e5e7eb",
                backgroundColor: "#ffffff",
                minHeight: 40,
                gap: 2,
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: "#6b7280",
                  fontSize: "0.75rem",
                }}
              >
                {selectedControls.size + Object.values(selectedChecks).reduce((sum, set) => sum + set.size, 0)} row(s) selected
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2, ml: "auto" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Typography
                  variant="caption"
                  sx={{ color: "#6b7280", fontSize: "0.75rem" }}
                >
                  Rows per page:
                </Typography>
                <FormControl size="small" sx={{ minWidth: 80 }}>
                  <Select
                    value={pageSize}
                    onChange={handlePageSizeChange}
                    sx={{
                      fontSize: "0.75rem",
                      height: "28px",
                      "& .MuiSelect-select": {
                        py: 0.5,
                        px: 1,
                      },
                    }}
                  >
                    <MenuItem value={10}>10</MenuItem>
                    <MenuItem value={25}>25</MenuItem>
                    <MenuItem value={50}>50</MenuItem>
                    <MenuItem value={100}>100</MenuItem>
                  </Select>
                </FormControl>
              </Box>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Typography
                  variant="caption"
                  sx={{ color: "#374151", fontSize: "0.75rem", fontWeight: 500 }}
                >
                  Page {page} of {totalPages || 1}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <IconButton
                  size="small"
                  onClick={() => setPage(1)}
                  disabled={page === 1}
                  sx={{
                    padding: "4px",
                    color: page === 1 ? "#d1d5db" : "#64748b",
                    "&:hover": {
                      backgroundColor: page === 1 ? "transparent" : "#f1f5f9",
                    },
                  }}
                >
                  <FirstPageIcon sx={{ fontSize: 18 }} />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => setPage(Math.max(1, page - 1))}
                  disabled={page === 1}
                  sx={{
                    padding: "4px",
                    color: page === 1 ? "#d1d5db" : "#64748b",
                    "&:hover": {
                      backgroundColor: page === 1 ? "transparent" : "#f1f5f9",
                    },
                  }}
                >
                  <ArrowBackIcon sx={{ fontSize: 16 }} />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => setPage(Math.min(totalPages, page + 1))}
                  disabled={page >= totalPages}
                  sx={{
                    padding: "4px",
                    color: page >= totalPages ? "#d1d5db" : "#64748b",
                    "&:hover": {
                      backgroundColor:
                        page >= totalPages ? "transparent" : "#f1f5f9",
                    },
                  }}
                >
                  <ArrowForwardIcon sx={{ fontSize: 16 }} />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => setPage(totalPages)}
                  disabled={page >= totalPages}
                  sx={{
                    padding: "4px",
                    color: page >= totalPages ? "#d1d5db" : "#64748b",
                    "&:hover": {
                      backgroundColor:
                        page >= totalPages ? "transparent" : "#f1f5f9",
                    },
                  }}
                >
                  <LastPageIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Box>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Checks Drawer */}
      <Drawer
        anchor="right"
        open={checksDrawerOpen}
        onClose={handleChecksDrawerClose}
        PaperProps={{
          sx: {
            width: { xs: "100%", sm: "90%", md: 800, lg: 1000, xl: 1200 },
            maxWidth: "100vw",
          },
        }}
      >
        <Box sx={{ p: 3, minHeight: "100vh", background: "#F9F9F9" }}>
          {/* Header */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 3,
              borderBottom: "1px solid #e2e8f0",
              pb: 2,
            }}
          >
            <Box>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 500,
                  color: "#1e293b",
                  fontSize: "1.125rem",
                  mb: 0.5,
                }}
              >
                Checks for Control: <b>{selectedControl?.control_code || "N/A"}</b>
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: "#64748b",
                  fontSize: "0.875rem",
                }}
              >
                {selectedControl?.title || "N/A"}
              </Typography>
            </Box>
            <IconButton onClick={handleChecksDrawerClose} sx={{ color: "#64748b" }}>
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Filters */}
          <Box
            sx={{
              mb: 3,
              p: 2,
              backgroundColor: "#f8fafc",
              borderRadius: "8px",
              border: "1px solid #e2e8f0",
            }}
          >
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 2, alignItems: "center", justifyContent: "space-between" }}>
              <Box sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
                <TextField
                  size="small"
                  placeholder="Search checks..."
                  value={checksSearchTerm}
                  onChange={(e) => {
                    setChecksSearchTerm(e.target.value);
                    setChecksPage(0);
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ fontSize: 18, color: "#64748b" }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    width: 300,
                    "& .MuiOutlinedInput-root": {
                      fontSize: "0.875rem",
                      backgroundColor: "white",
                      borderRadius: "6px",
                    },
                  }}
                />
                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <InputLabel>Severity</InputLabel>
                  <Select
                    value={checksSeverity}
                    label="Severity"
                    onChange={(e) => {
                      setChecksSeverity(e.target.value);
                      setChecksPage(0);
                    }}
                    sx={{
                      fontSize: "0.875rem",
                      backgroundColor: "white",
                    }}
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="CRITICAL">Critical</MenuItem>
                    <MenuItem value="HIGH">High</MenuItem>
                    <MenuItem value="MEDIUM">Medium</MenuItem>
                    <MenuItem value="LOW">Low</MenuItem>
                    <MenuItem value="INFORMATIONAL">Informational</MenuItem>
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <InputLabel>Service</InputLabel>
                  <Select
                    value={checksService}
                    label="Service"
                    onChange={(e) => {
                      setChecksService(e.target.value);
                      setChecksPage(0);
                    }}
                    sx={{
                      fontSize: "0.875rem",
                      backgroundColor: "white",
                    }}
                  >
                    <MenuItem value="">All</MenuItem>
                    {availableServices.map((service) => (
                      <MenuItem key={service} value={service}>
                        {service}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
              <Button
                variant="outlined"
                startIcon={<SaveIcon sx={{ fontSize: 18 }} />}
                onClick={handleSaveChecksSuppressions}
                disabled={isSavingSuppressions || !hasChecksStateChanged}
                  sx={{
                    color: "#374151",
                    borderColor: "#e2e8f0",
                    textTransform: "none",
                    fontSize: "0.875rem",
                    fontWeight: 500,
                    px: 2,
                    py: 0.75,
                    borderRadius: "6px",
                    backgroundColor: "transparent",
                    "&:hover": {
                      backgroundColor: "#f8fafc",
                      borderColor: "#d1d5db",
                      color: "#1e293b",
                    },
                    "&:disabled": {
                      backgroundColor: "transparent",
                      borderColor: "#e5e7eb",
                      color: "#9ca3af",
                    },
                  }}
                >
                  Save
                </Button>
            </Box>
          </Box>

          {/* Loading State */}
          {isFetchingChecks && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                py: 8,
              }}
            >
              <CircularProgress size={40} sx={{ color: "#3b82f6" }} />
              <Typography
                variant="body2"
                sx={{
                  ml: 2,
                  color: "#64748b",
                  fontSize: "0.875rem",
                  fontWeight: 500,
                }}
              >
                Loading checks...
              </Typography>
            </Box>
          )}

          {/* Error State */}
          {fetchChecksError && !isFetchingChecks && (
            <Box
              sx={{
                mb: 4,
                p: 3,
                backgroundColor: "#fef2f2",
                borderRadius: "8px",
                border: "1px solid #f87171",
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: "#991b1b",
                  fontSize: "0.875rem",
                  fontWeight: 500,
                }}
              >
                {getErrorMessage(fetchChecksError)}
              </Typography>
            </Box>
          )}

          {/* Checks Table */}
          {allChecks && !isFetchingChecks && !fetchChecksError && (
            <Card
              sx={{
                borderRadius: "8px",
                border: "1px solid #e2e8f0",
                boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
              }}
            >
              <CardContent sx={{ p: 0 }}>
                <TableContainer
                  sx={{
                    maxHeight: "calc(100vh - 400px)",
                    overflowY: "auto",
                    "&::-webkit-scrollbar": {
                      height: "8px",
                      width: "8px",
                    },
                    "&::-webkit-scrollbar-track": {
                      backgroundColor: "#f1f5f9",
                    },
                    "&::-webkit-scrollbar-thumb": {
                      backgroundColor: "#cbd5e1",
                      borderRadius: "4px",
                      "&:hover": {
                        backgroundColor: "#94a3b8",
                      },
                    },
                  }}
                >
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow sx={{ backgroundColor: "#f8fafc" }}>
                        <TableCell
                          sx={{
                            fontWeight: 600,
                            color: "#374151",
                            fontSize: "0.75rem",
                            py: 1,
                            width: 50,
                          }}
                        >
                          <Checkbox
                            indeterminate={
                              selectedChecks[selectedControl?.control_id] &&
                              selectedChecks[selectedControl?.control_id].size > 0 &&
                              selectedChecks[selectedControl?.control_id].size < filteredChecks.items.length
                            }
                            checked={
                              selectedControls.has(selectedControl?.control_id) ||
                              (selectedChecks[selectedControl?.control_id] &&
                               selectedChecks[selectedControl?.control_id].size === filteredChecks.items.length &&
                               filteredChecks.items.length > 0)
                            }
                            onChange={(e) => {
                              if (e.target.checked) {
                                // Select all checks
                                const allCheckIds = new Set(filteredChecks.items.map((check) => check.check_id));
                                setSelectedChecks((prev) => ({
                                  ...prev,
                                  [selectedControl.control_id]: allCheckIds,
                                }));
                                // Also select control
                                setSelectedControls((prev) => {
                                  const newSet = new Set(prev);
                                  newSet.add(selectedControl.control_id);
                                  return newSet;
                                });
                              } else {
                                // Deselect all
                                setSelectedChecks((prev) => {
                                  const newChecks = { ...prev };
                                  delete newChecks[selectedControl.control_id];
                                  return newChecks;
                                });
                                setSelectedControls((prev) => {
                                  const newSet = new Set(prev);
                                  newSet.delete(selectedControl.control_id);
                                  return newSet;
                                });
                              }
                            }}
                            size="small"
                          />
                        </TableCell>
                        <TableCell
                          sx={{
                            fontWeight: 600,
                            color: "#374151",
                            fontSize: "0.75rem",
                            py: 1,
                          }}
                        >
                          Title
                        </TableCell>
                        {/* <TableCell
                          sx={{
                            fontWeight: 600,
                            color: "#374151",
                            fontSize: "0.75rem",
                            py: 1,
                          }}
                        >
                          Check ID
                        </TableCell> */}
                        <TableCell
                          sx={{
                            fontWeight: 600,
                            color: "#374151",
                            fontSize: "0.75rem",
                            py: 1,
                          }}
                        >
                          Service
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {paginatedChecks && paginatedChecks.length > 0 ? (
                        paginatedChecks.map((check, index) => {
                          const sevColor = SEVERITY_COLORS[(check.severity || '').toUpperCase()] || SEVERITY_COLORS.DEFAULT;
                          return (
                            <TableRow
                              key={check.check_id || index}
                              sx={{
                                "&:hover": {
                                  backgroundColor: "#f8fafc",
                                  "& td": {
                                    backgroundColor: "#f8fafc",
                                  },
                                },
                              }}
                            >
                              <TableCell sx={{ py: 1 }}>
                                <Checkbox
                                  checked={
                                    selectedControls.has(selectedControl?.control_id) ||
                                    (selectedChecks[selectedControl?.control_id]?.has(check.check_id) || false)
                                  }
                                  onChange={(e) => handleCheckCheckboxToggle(check.check_id, e.target.checked)}
                                  size="small"
                                />
                              </TableCell>
                              <TableCell sx={{ py: 1 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                  <Box sx={{ width: 10, height: 10, borderRadius: '50%', background: sevColor, display: 'inline-block', mr: 1 }} />
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      color: "#374151",
                                      fontSize: "0.75rem",
                                      fontWeight: 500,
                                    }}
                                  >
                                    {check.title || "N/A"}
                                  </Typography>
                                </Box>
                              </TableCell>
                              {/* <TableCell sx={{ py: 1 }}>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    color: "#374151",
                                    fontSize: "0.75rem",
                                  }}
                                >
                                  {check.check_id || "N/A"}
                                </Typography>
                              </TableCell> */}
                              <TableCell sx={{ py: 1 }}>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    color: "#374151",
                                    fontSize: "0.75rem",
                                  }}
                                >
                                  {check.service || "N/A"}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          );
                        })
                      ) : (
                        <TableRow>
                          <TableCell colSpan={4} sx={{ py: 4, textAlign: "center" }}>
                            <Typography
                              variant="body2"
                              sx={{
                                color: "#64748b",
                                fontSize: "0.875rem",
                              }}
                            >
                              No checks found
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>

                {/* Footer: Severity legend and pagination */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    px: 2,
                    py: 1,
                    borderTop: "1px solid #e2e8f0",
                    backgroundColor: "#f8fafc",
                    minHeight: 40,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "#6b7280",
                        fontSize: "0.75rem",
                      }}
                    >
                      {selectedControls.has(selectedControl?.control_id)
                        ? filteredChecks.items.length
                        : (selectedChecks[selectedControl?.control_id]?.size || 0)} row(s) selected
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography variant="caption" sx={{ color: "#64748b", fontSize: "0.75rem", mr: 1 }}>
                      Severity Levels:
                    </Typography>
                    {['CRITICAL', 'HIGH', 'MEDIUM', 'LOW', 'INFORMATIONAL'].map((sev) => (
                      <React.Fragment key={sev}>
                        <Box sx={{ width: 10, height: 10, borderRadius: '50%', background: SEVERITY_COLORS[sev], display: 'inline-block', mr: 0.5 }} />
                        <Typography variant="caption" sx={{ color: SEVERITY_COLORS[sev], fontSize: "0.75rem", mr: 1 }}>
                          {sev.charAt(0) + sev.slice(1).toLowerCase()}
                        </Typography>
                      </React.Fragment>
                    ))}
                    </Box>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <Typography
                      variant="caption"
                      sx={{ color: "#64748b", fontSize: "0.75rem", mr: 1 }}
                    >
                      {filteredChecks.total === 0 ? 0 : checksPage * checksPageSize + 1}–{Math.min((checksPage + 1) * checksPageSize, filteredChecks.total)} of {filteredChecks.total}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => handleChecksPageChange(Math.max(checksPage - 1, 0))}
                      disabled={checksPage === 0}
                      sx={{
                        padding: "4px",
                        color: checksPage === 0 ? "#d1d5db" : "#64748b",
                        "&:hover": {
                          backgroundColor: checksPage === 0 ? "transparent" : "#f1f5f9",
                        },
                      }}
                    >
                      <ArrowBackIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleChecksPageChange(Math.min(checksPage + 1, Math.ceil(filteredChecks.total / checksPageSize) - 1))}
                      disabled={checksPage >= Math.ceil(filteredChecks.total / checksPageSize) - 1}
                      sx={{
                        padding: "4px",
                        color: checksPage >= Math.ceil(filteredChecks.total / checksPageSize) - 1 ? "#d1d5db" : "#64748b",
                        "&:hover": {
                          backgroundColor:
                            checksPage >= Math.ceil(filteredChecks.total / checksPageSize) - 1 ? "transparent" : "#f1f5f9",
                        },
                      }}
                    >
                      <ArrowForwardIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          )}
        </Box>
      </Drawer>

      {/* Snackbar for notifications - MUI Snackbar (not notistack) */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Save Status Dialog for Controls */}
      <Dialog
        open={saveDialogOpen}
        onClose={() => {
          if (saveStatus.step === 4 || saveStatus.error) {
            setSaveDialogOpen(false);
            setSaveStatus({
              step: 0,
              deleteMessage: "",
              createMessage: "",
              error: null,
            });
          }
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "12px",
            boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
            border: "1px solid #e2e8f0",
            overflow: "hidden",
          },
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            bgcolor: "#f8fafc",
            padding: "20px 24px",
            borderBottom: "1px solid #e2e8f0",
          }}
        >
          <Box>
            <Typography
              variant="h6"
              component="h2"
              sx={{
                fontWeight: 600,
                color: "#1e293b",
                fontSize: "1.125rem",
                mb: 0.5,
              }}
            >
              {saveStatus.error ? "Error Saving Suppressions" : "Saving Suppressions"}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: "#64748b",
                fontSize: "0.875rem",
                fontWeight: 400,
              }}
            >
              {saveStatus.error ? "An error occurred while saving" : "Processing your suppression changes"}
            </Typography>
          </Box>
          <IconButton
            onClick={() => {
              if (saveStatus.step === 4 || saveStatus.error) {
                setSaveDialogOpen(false);
                setSaveStatus({
                  step: 0,
                  deleteMessage: "",
                  createMessage: "",
                  error: null,
                });
              }
            }}
            disabled={saveStatus.step > 0 && saveStatus.step < 4 && !saveStatus.error}
            sx={{
              color: "#64748b",
              "&:hover": {
                backgroundColor: "#f1f5f9",
                color: "#374151",
              },
              "&:disabled": {
                opacity: 0.5,
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Content */}
        <DialogContent sx={{ padding: "24px" }}>
          {saveStatus.error ? (
            <DialogContentText sx={{ color: "error.main", mb: 2 }}>
              {saveStatus.error}
            </DialogContentText>
          ) : (
            <>
              {saveStatus.step > 0 && saveStatus.deleteMessage && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="body2" sx={{ mb: 1, fontWeight: 600, color: "#1e293b" }}>
                    {saveStatus.deleteMessage.includes("Resetting") ? "Resetting All Suppressions" : "Step 1: Unselecting Controls"}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#64748b", mb: 1.5 }}>
                    {saveStatus.deleteMessage}
                  </Typography>
                  {saveStatus.step === 1 && <LinearProgress sx={{ mt: 1, borderRadius: 1 }} />}
                </Box>
              )}
              {saveStatus.step > 1 && saveStatus.createMessage && !saveStatus.createMessage.includes("Refreshing") && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="body2" sx={{ mb: 1, fontWeight: 600, color: "#1e293b" }}>
                    Step 2: Suppressing Controls
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#64748b", mb: 1.5 }}>
                    {saveStatus.createMessage}
                  </Typography>
                  {saveStatus.step === 2 && <LinearProgress sx={{ mt: 1, borderRadius: 1 }} />}
                </Box>
              )}
              {saveStatus.step === 2 && saveStatus.createMessage && saveStatus.createMessage.includes("Refreshing") && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="body2" sx={{ mb: 1, fontWeight: 600, color: "#1e293b" }}>
                    Refreshing Data
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#64748b", mb: 1.5 }}>
                    {saveStatus.createMessage}
                  </Typography>
                  <LinearProgress sx={{ mt: 1, borderRadius: 1 }} />
                </Box>
              )}
              {saveStatus.step === 3 && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="body2" sx={{ mb: 1, fontWeight: 600, color: "#1e293b" }}>
                    Refreshing Data
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#64748b", mb: 1.5 }}>
                    Refreshing controls...
                  </Typography>
                  <LinearProgress sx={{ mt: 1, borderRadius: 1 }} />
                </Box>
              )}
              {saveStatus.step === 4 && (
                <Box sx={{ textAlign: "center", py: 2 }}>
                  <Typography variant="body1" sx={{ color: "#10b981", fontWeight: 500 }}>
                    All operations completed successfully!
                  </Typography>
                </Box>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Save Status Dialog for Checks */}
      <Dialog
        open={saveChecksDialogOpen}
        onClose={() => {
          // Allow closing if done (step > 3 means refresh completed) or if there's an error
          if (saveChecksStatus.step > 3 || saveChecksStatus.error) {
            setSaveChecksDialogOpen(false);
            setSaveChecksStatus({
              step: 0,
              deleteMessage: "",
              createMessage: "",
              error: null,
            });
          }
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "12px",
            boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
            border: "1px solid #e2e8f0",
            overflow: "hidden",
          },
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            bgcolor: "#f8fafc",
            padding: "20px 24px",
            borderBottom: "1px solid #e2e8f0",
          }}
        >
          <Box>
            <Typography
              variant="h6"
              component="h2"
              sx={{
                fontWeight: 600,
                color: "#1e293b",
                fontSize: "1.125rem",
                mb: 0.5,
              }}
            >
              {saveChecksStatus.error ? "Error Saving Suppressions" : "Saving Suppressions"}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: "#64748b",
                fontSize: "0.875rem",
                fontWeight: 400,
              }}
            >
              {saveChecksStatus.error ? "An error occurred while saving" : "Processing your check suppression changes"}
            </Typography>
          </Box>
          <IconButton
            onClick={() => {
              // Allow closing if done (step > 3 means refresh completed) or if there's an error
              if (saveChecksStatus.step > 3 || saveChecksStatus.error) {
                setSaveChecksDialogOpen(false);
                setSaveChecksStatus({
                  step: 0,
                  deleteMessage: "",
                  createMessage: "",
                  error: null,
                });
              }
            }}
            disabled={saveChecksStatus.step > 0 && saveChecksStatus.step <= 3 && !saveChecksStatus.error}
            sx={{
              color: "#64748b",
              "&:hover": {
                backgroundColor: "#f1f5f9",
                color: "#374151",
              },
              "&:disabled": {
                opacity: 0.5,
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Content */}
        <DialogContent sx={{ padding: "24px" }}>
          {saveChecksStatus.error ? (
            <DialogContentText sx={{ color: "error.main", mb: 2 }}>
              {saveChecksStatus.error}
            </DialogContentText>
          ) : (
            <>
              {saveChecksStatus.step > 0 && saveChecksStatus.deleteMessage && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="body2" sx={{ mb: 1, fontWeight: 600, color: "#1e293b" }}>
                    Step 1: Unselecting Checks
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#64748b", mb: 1.5 }}>
                    {saveChecksStatus.deleteMessage}
                  </Typography>
                  {saveChecksStatus.step === 1 && <LinearProgress sx={{ mt: 1, borderRadius: 1 }} />}
                </Box>
              )}
              {saveChecksStatus.step > 1 && saveChecksStatus.createMessage && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="body2" sx={{ mb: 1, fontWeight: 600, color: "#1e293b" }}>
                    Step 2: Suppressing Checks
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#64748b", mb: 1.5 }}>
                    {saveChecksStatus.createMessage}
                  </Typography>
                  {saveChecksStatus.step === 2 && <LinearProgress sx={{ mt: 1, borderRadius: 1 }} />}
                </Box>
              )}
              {saveChecksStatus.step > 2 && saveChecksStatus.step <= 3 && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="body2" sx={{ mb: 1, fontWeight: 600, color: "#1e293b" }}>
                    Refreshing Data
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#64748b", mb: 1.5 }}>
                    Refreshing checks...
                  </Typography>
                  <LinearProgress sx={{ mt: 1, borderRadius: 1 }} />
                </Box>
              )}
              {saveChecksStatus.step > 3 && (
                <Box sx={{ textAlign: "center", py: 2 }}>
                  <Typography variant="body1" sx={{ color: "#10b981", fontWeight: 500 }}>
                    All operations completed successfully!
                  </Typography>
                </Box>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Reset Confirmation Dialog */}
      <Dialog
        open={resetConfirmDialogOpen}
        onClose={() => setResetConfirmDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "12px",
            boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
            border: "1px solid #e2e8f0",
            overflow: "hidden",
          },
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: 600,
            color: "#1e293b",
            fontSize: "1.125rem",
            pb: 1,
          }}
        >
          Confirm Reset
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: "#64748b", fontSize: "0.875rem" }}>
            Are you sure you want to reset all the suppressions?
          </DialogContentText>
        </DialogContent>
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 1.5,
            padding: "16px 24px",
            borderTop: "1px solid #e2e8f0",
          }}
        >
          <Button
            onClick={() => setResetConfirmDialogOpen(false)}
            sx={{
              color: "#64748b",
              textTransform: "none",
              fontSize: "0.875rem",
              fontWeight: 500,
              "&:hover": {
                backgroundColor: "#f1f5f9",
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleResetSuppressions}
            variant="contained"
            sx={{
              backgroundColor: "#ef4444",
              color: "white",
              textTransform: "none",
              fontSize: "0.875rem",
              fontWeight: 500,
              "&:hover": {
                backgroundColor: "#dc2626",
              },
            }}
          >
            Reset
          </Button>
        </Box>
      </Dialog>

      {/* Schedule Selection Dialog */}
      <Dialog
        open={scheduleSelectionDialogOpen}
        onClose={() => {
          setScheduleSelectionDialogOpen(false);
          setSelectedSchedulesToApply(new Set());
          setPendingSaveAction(null);
        }}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "12px",
            boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
            border: "1px solid #e2e8f0",
            overflow: "hidden",
          },
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            bgcolor: "#f8fafc",
            padding: "20px 24px",
            borderBottom: "1px solid #e2e8f0",
          }}
        >
          <Box>
            <Typography
              variant="h6"
              component="h2"
              sx={{
                fontWeight: 600,
                color: "#1e293b",
                fontSize: "1.125rem",
                mb: 0.5,
              }}
            >
              Apply to Other Schedules
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: "#64748b",
                fontSize: "0.875rem",
                fontWeight: 400,
              }}
            >
              Do you want to apply the same Suppression Configuration to all the other schedules?
            </Typography>
          </Box>
          <IconButton
            onClick={() => {
              setScheduleSelectionDialogOpen(false);
              setSelectedSchedulesToApply(new Set());
              setPendingSaveAction(null);
            }}
            sx={{
              color: "#64748b",
              "&:hover": {
                backgroundColor: "#f1f5f9",
                color: "#374151",
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Content */}
        <DialogContent sx={{ padding: "24px" }}>
          {isFetchingSchedules ? (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 4 }}>
              <CircularProgress size={40} sx={{ color: "#3b82f6" }} />
              <Typography variant="body2" sx={{ ml: 2, color: "#64748b" }}>
                Loading schedules...
              </Typography>
            </Box>
          ) : availableSchedules.length === 0 ? (
            <Typography variant="body2" sx={{ color: "#64748b", textAlign: "center", py: 4 }}>
              No other schedules found for this account.
            </Typography>
          ) : (
            <Box>
              <TableContainer
                sx={{
                  maxHeight: 400,
                  border: "1px solid #e2e8f0",
                  borderRadius: "8px",
                }}
              >
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: "#f8fafc" }}>
                      <TableCell sx={{ width: 50, py: 1 }}>
                        <Checkbox
                          indeterminate={
                            selectedSchedulesToApply.size > 0 &&
                            selectedSchedulesToApply.size < availableSchedules.length
                          }
                          checked={
                            availableSchedules.length > 0 &&
                            selectedSchedulesToApply.size === availableSchedules.length
                          }
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedSchedulesToApply(
                                new Set(availableSchedules.map((s) => s.schedule_id))
                              );
                            } else {
                              setSelectedSchedulesToApply(new Set());
                            }
                          }}
                          size="small"
                        />
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 600,
                          color: "#374151",
                          fontSize: "0.75rem",
                          py: 1,
                        }}
                      >
                        Schedule Name
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {availableSchedules.map((schedule) => (
                      <TableRow
                        key={schedule.schedule_id}
                        sx={{
                          "&:hover": {
                            backgroundColor: "#f8fafc",
                          },
                        }}
                      >
                        <TableCell sx={{ py: 1 }}>
                          <Checkbox
                            checked={selectedSchedulesToApply.has(schedule.schedule_id)}
                            onChange={(e) => {
                              const newSet = new Set(selectedSchedulesToApply);
                              if (e.target.checked) {
                                newSet.add(schedule.schedule_id);
                              } else {
                                newSet.delete(schedule.schedule_id);
                              }
                              setSelectedSchedulesToApply(newSet);
                            }}
                            size="small"
                          />
                        </TableCell>
                        <TableCell sx={{ py: 1 }}>
                          <Typography variant="body2" sx={{ fontSize: "0.875rem", color: "#374151" }}>
                            {formatScheduleName(schedule)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              {selectedSchedulesToApply.size > 0 && (
                <Typography
                  variant="body2"
                  sx={{ color: "#64748b", fontSize: "0.75rem", mt: 2, textAlign: "center" }}
                >
                  {selectedSchedulesToApply.size} schedule(s) selected
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>

        {/* Footer */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 1.5,
            padding: "16px 24px",
            borderTop: "1px solid #e2e8f0",
          }}
        >
          <Button
            onClick={() => {
              setScheduleSelectionDialogOpen(false);
              setSelectedSchedulesToApply(new Set());
              setPendingSaveAction(null);
            }}
            sx={{
              color: "#64748b",
              textTransform: "none",
              fontSize: "0.875rem",
              fontWeight: 500,
              "&:hover": {
                backgroundColor: "#f1f5f9",
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              const targetScheduleIds = Array.from(selectedSchedulesToApply);
              setScheduleSelectionDialogOpen(false);
              
              if (pendingSaveAction === 'controls') {
                performSaveSuppressions(targetScheduleIds);
              } else if (pendingSaveAction === 'checks') {
                performSaveChecksSuppressions(targetScheduleIds);
              }
              
              setSelectedSchedulesToApply(new Set());
              setPendingSaveAction(null);
            }}
            variant="contained"
            sx={{
              backgroundColor: "#3b82f6",
              color: "white",
              textTransform: "none",
              fontSize: "0.875rem",
              fontWeight: 500,
              "&:hover": {
                backgroundColor: "#2563eb",
              },
            }}
          >
            Save
          </Button>
        </Box>
      </Dialog>
    </Box>
  );
};

export default Controls;

